import { useState } from "react"
import { 
  LayoutDashboard, 
  Calendar, 
  BarChart3, 
  Database,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User
} from "lucide-react"
import { NavLink, useLocation } from "react-router-dom"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useIsMobile } from "@/hooks/use-mobile"
import { useAuth } from "@/hooks/useAuth"

const workspaceItems = [
  {
    title: "首頁",
    url: "/",
    icon: LayoutDashboard,
    description: "今日概覽看板"
  },
  {
    title: "工作流程",
    url: "/workflow",
    icon: BarChart3,
    description: "流程階段管理"
  },
  {
    title: "部門日曆",
    url: "/calendar",
    icon: Calendar,
    description: "部門工作日曆"
  }
]

const settingsItems = [
  {
    title: "基礎資料",
    url: "/master-data",
    icon: Database,
    description: "系統基礎資料管理"
  },
  {
    title: "系統設定",
    url: "/settings",
    icon: Settings,
    description: "工作流程設定管理"
  },
  {
    title: "用戶管理",
    url: "/user-management",
    icon: User,
    description: "用戶帳號權限管理"
  }
]

interface AppSidebarProps {
  collapsed: boolean
  onToggle: () => void
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const location = useLocation()
  const currentPath = location.pathname
  const isMobile = useIsMobile()
  const { user, signOut, isAdmin } = useAuth()

  const isActive = (path: string) => {
    if (path === "/" && currentPath === "/") return true
    return path !== "/" && currentPath.startsWith(path)
  }

  return (
    <div className={cn(
      "bg-card border-r border-border transition-all duration-300 h-screen flex flex-col rounded-r-2xl overflow-hidden",
      collapsed ? (isMobile ? "w-0 overflow-hidden" : "w-16") : "w-64",
      isMobile && !collapsed && "fixed inset-y-0 left-0 z-50 shadow-lg"
    )}>
      {/* Header */}
      <div className={cn(
        "flex items-center justify-between border-b border-border",
        collapsed && !isMobile ? "p-2" : "p-4"
      )}>
        {(!collapsed || isMobile) && (
          <div className={cn("flex items-center gap-3", isMobile && collapsed && "hidden")}>
            <img 
              src="/favicon.png" 
              alt="Logo" 
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-lg font-semibold text-foreground">AI 生管</h1>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className={cn(
            "h-8 w-8 p-0 touch-none",
            collapsed && !isMobile && "mx-auto"
          )}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-4">
        {/* Workspace Section */}
        <div>
          {(!collapsed || isMobile) && (
            <div className="px-3 py-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                工作區
              </h3>
            </div>
          )}
          <ul className="space-y-1">
            {workspaceItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item.url)
              
              return (
                <li key={item.url}>
                  <NavLink
                    to={item.url}
                    onClick={() => isMobile && onToggle()}
                    className={cn(
                      "flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors touch-none",
                      "hover:bg-accent hover:text-accent-foreground",
                      "min-h-[44px]", // Better touch targets on mobile
                      active 
                        ? "bg-primary text-primary-foreground shadow-modern-sm" 
                        : "text-muted-foreground"
                    )}
                  >
                    <Icon className={cn(
                      "h-5 w-5", 
                      collapsed && !isMobile ? "mx-auto" : "mr-3"
                    )} />
                    {(!collapsed || isMobile) && (
                      <div className="flex-1">
                        <div className="font-medium">{item.title}</div>
                        <div className="text-xs opacity-75">{item.description}</div>
                      </div>
                    )}
                  </NavLink>
                </li>
              )
            })}
          </ul>
        </div>

        {/* Settings Section */}
        <div>
          {(!collapsed || isMobile) && (
            <div className="px-3 py-2">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                設定
              </h3>
            </div>
          )}
          <ul className="space-y-1">
            {settingsItems
              .filter((item) => {
                // Only show User Management for admin users
                if (item.url === "/user-management") {
                  return isAdmin;
                }
                return true;
              })
              .map((item) => {
                const Icon = item.icon
                const active = isActive(item.url)
                
                return (
                  <li key={item.url}>
                    <NavLink
                      to={item.url}
                      onClick={() => isMobile && onToggle()}
                      className={cn(
                        "flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors touch-none",
                        "hover:bg-accent hover:text-accent-foreground",
                        "min-h-[44px]", // Better touch targets on mobile
                        active 
                          ? "bg-primary text-primary-foreground shadow-modern-sm" 
                          : "text-muted-foreground"
                      )}
                    >
                      <Icon className={cn(
                        "h-5 w-5", 
                        collapsed && !isMobile ? "mx-auto" : "mr-3"
                      )} />
                      {(!collapsed || isMobile) && (
                        <div className="flex-1">
                          <div className="font-medium">{item.title}</div>
                          <div className="text-xs opacity-75">{item.description}</div>
                        </div>
                      )}
                    </NavLink>
                  </li>
                )
              })}
          </ul>
        </div>
      </nav>

      {/* User Profile & Footer */}
      <div className="border-t border-border">
        {(!collapsed || isMobile) && (
          <div className="p-3 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-primary-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">
                  {user?.email}
                </div>
                <div className="text-xs text-muted-foreground">已登入</div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={signOut}
                className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                title="登出"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
        
        {(!collapsed || isMobile) && (
          <div className="p-4">
            <div className="text-xs text-muted-foreground">
              <div>© {new Date().getFullYear()} AI 生管 • v1.0.0</div>
            </div>
          </div>
        )}
        
        {collapsed && !isMobile && (
          <div className="p-2 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={signOut}
              className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
              title="登出"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
